package br.com.htms.inspeccaocampo.data.nrvisual

/**
 * Mapa objeto detectado -> pacotes/IDs NR permitidos.
 * Usado para filtrar alucinacoes da IA e o fallback local por keywords.
 */
object NrVisualObjectMap {

    /** Prefixo ou id exato permitido por objeto (lowercase). */
    private val mapa: Map<String, List<String>> = mapOf(
        "extintor" to listOf("NR23_", "GERAL_hidrante", "NR23_rota", "NR23_sinal"),
        "hidrante" to listOf("GERAL_hidrante", "NR23_"),
        "sinal_saida" to listOf("NR23_sinal", "NR23_rota"),
        "rota_fuga" to listOf("NR23_rota", "NR23_sinal"),
        "capacete" to listOf("NR06_capacete", "NR06_epi"),
        "oculos" to listOf("NR06_oculos", "NR06_epi"),
        "luva" to listOf("NR06_luva", "NR06_epi", "NR10_epi"),
        "bota" to listOf("NR06_bota", "NR06_epi"),
        "colete" to listOf("NR06_colete", "NR06_epi"),
        "epi" to listOf("NR06_"),
        "pessoa" to listOf("NR06_", "NR35_cinto", "GERAL_protetor", "GERAL_trabalhador"),
        "trabalhador" to listOf("NR06_", "NR35_", "GERAL_trabalhador", "GERAL_protetor"),
        "painel" to listOf("NR10_"),
        "quadro_eletrico" to listOf("NR10_"),
        "cabo" to listOf("NR10_cabos", "NR10_extensao", "NR10_", "NR18_circulacao"),
        "andaime" to listOf("NR18_andaime", "NR18_guarda", "NR18_queda", "NR35_"),
        "escada" to listOf("NR18_escada", "NR35_escada"),
        "plataforma" to listOf("NR35_plataforma", "NR18_andaime"),
        "cinto" to listOf("NR35_"),
        "abertura_piso" to listOf("NR18_abertura"),
        "vala" to listOf("GERAL_pocos", "NR18_area", "NR18_tapume"),
        "maquina" to listOf("NR12_"),
        "torno" to listOf("NR12_"),
        "solda" to listOf("GERAL_protetor", "NR06_", "GERAL_trabalhador"),
        "prensa" to listOf("NR12_"),
        "serra" to listOf("NR12_"),
        "placa" to listOf("NR26_"),
        "piso" to listOf("NR26_cor", "NR18_abertura", "NR18_circulacao"),
        "passagem" to listOf("NR18_circulacao", "NR23_rota"),
        "corredor" to listOf("NR18_circulacao", "NR23_rota", "GERAL_empilhamento"),
        "palete" to listOf("GERAL_empilhamento", "NR18_circulacao", "NR23_extintor"),
        "produto_quimico" to listOf("GERAL_produto"),
        "tapume" to listOf("NR18_tapume", "NR18_area"),
        "ferramenta" to listOf("GERAL_ferramenta", "NR18_queda")
    )

    private val cenaPorObjeto: Map<String, NrCenaTipo> = mapOf(
        "extintor" to NrCenaTipo.INCENDIO,
        "hidrante" to NrCenaTipo.INCENDIO,
        "sinal_saida" to NrCenaTipo.INCENDIO,
        "rota_fuga" to NrCenaTipo.INCENDIO,
        "painel" to NrCenaTipo.ELETRICA,
        "quadro_eletrico" to NrCenaTipo.ELETRICA,
        "cabo" to NrCenaTipo.ELETRICA,
        "andaime" to NrCenaTipo.OBRA,
        "escada" to NrCenaTipo.ALTURA,
        "plataforma" to NrCenaTipo.ALTURA,
        "cinto" to NrCenaTipo.ALTURA,
        "maquina" to NrCenaTipo.OFICINA,
        "torno" to NrCenaTipo.OFICINA,
        "solda" to NrCenaTipo.OFICINA,
        "prensa" to NrCenaTipo.OFICINA,
        "serra" to NrCenaTipo.OFICINA,
        "passagem" to NrCenaTipo.CIRCULACAO,
        "corredor" to NrCenaTipo.CIRCULACAO,
        "capacete" to NrCenaTipo.OBRA,
        "trabalhador" to NrCenaTipo.OBRA,
        "pessoa" to NrCenaTipo.OBRA
    )

    /** Aliases de texto livre / IA -> chave canonica. */
    private val aliases: List<Pair<String, String>> = listOf(
        "extintor" to "extintor",
        "extinguisher" to "extintor",
        "hidrante" to "hidrante",
        "saida de emergencia" to "sinal_saida",
        "saida emergencia" to "sinal_saida",
        "rota de fuga" to "rota_fuga",
        "capacete" to "capacete",
        "oculos" to "oculos",
        "luva" to "luva",
        "bota" to "bota",
        "colete" to "colete",
        "epi" to "epi",
        "trabalhador" to "trabalhador",
        "pessoa" to "pessoa",
        "pedreiro" to "trabalhador",
        "painel" to "painel",
        "quadro eletrico" to "quadro_eletrico",
        "quadro elétrico" to "quadro_eletrico",
        "ccm" to "painel",
        "cabo" to "cabo",
        "extensoes" to "cabo",
        "desencapad" to "cabo",
        "extensao" to "cabo",
        "extensao" to "cabo",
        "andaime" to "andaime",
        "escada" to "escada",
        "plataforma" to "plataforma",
        "cinto" to "cinto",
        "talabarte" to "cinto",
        "abertura" to "abertura_piso",
        "buraco" to "abertura_piso",
        "vala" to "vala",
        "escavacao" to "vala",
        "maquina" to "maquina",
        "torno" to "maquina",
        "solda" to "solda",
        "soldador" to "solda",
        "mascara" to "solda",
        "máquina" to "maquina",
        "prensa" to "prensa",
        "serra" to "serra",
        "placa" to "placa",
        "sinalizacao" to "placa",
        "passagem" to "passagem",
        "corredor" to "corredor",
        "palete" to "palete",
        "paletes" to "palete",
        "quimico" to "produto_quimico",
        "bombona" to "produto_quimico",
        "tapume" to "tapume",
        "ferramenta" to "ferramenta"
    )

    fun normalizarObjeto(raw: String): String? {
        val t = raw.trim().lowercase()
        if (t.isBlank()) return null
        if (t in mapa) return t
        for ((alias, key) in aliases) {
            if (t == alias || t.contains(alias)) return key
        }
        return t.takeIf { it in mapa }
    }

    fun detectarObjetosNaDescricao(descricao: String): List<String> {
        val texto = descricao.lowercase()
        if (texto.isBlank()) return emptyList()
        val out = linkedSetOf<String>()
        for ((alias, key) in aliases) {
            if (alias in texto) out.add(key)
        }
        return out.toList()
    }

    fun cenaSugeridaParaObjetos(objetos: List<String>): NrCenaTipo? {
        for (o in objetos) {
            val key = normalizarObjeto(o) ?: continue
            cenaPorObjeto[key]?.let { return it }
        }
        return null
    }

    fun prefixesPermitidos(objetos: List<String>): Set<String> {
        val out = linkedSetOf<String>()
        for (o in objetos) {
            val key = normalizarObjeto(o) ?: continue
            mapa[key]?.let { out.addAll(it) }
        }
        return out
    }

    fun catalogIdPermitido(catalogId: String, objetos: List<String>): Boolean {
        if (objetos.isEmpty()) return true
        val prefixes = prefixesPermitidos(objetos)
        if (prefixes.isEmpty()) return true
        return prefixes.any { p -> catalogId.startsWith(p) || catalogId.contains(p) }
    }

    fun filtrarAchados(achados: List<NrAchado>, objetos: List<String>): List<NrAchado> {
        if (objetos.isEmpty()) return achados
        val filtrados = achados.filter { catalogIdPermitido(it.catalogId, objetos) }
        // Preferir filtrados; se filtro zerar mas havia achados, descartar alucinacoes (vazio).
        return filtrados
    }

    /** Extintor / incendio nunca deve mapear para NR-06. */
    fun reforcarRegrasDuras(achados: List<NrAchado>, objetos: List<String>): List<NrAchado> {
        val keys = objetos.mapNotNull { normalizarObjeto(it) }.toSet()
        val soIncendio = keys.isNotEmpty() && keys.all { it in setOf("extintor", "hidrante", "sinal_saida", "rota_fuga") }
        if (!soIncendio && "extintor" !in keys && "hidrante" !in keys) return achados
        return achados.filter { !it.catalogId.startsWith("NR06_") }
    }
}